import React, {FC, useCallback, useEffect, useMemo, useState} from "react";
import {parse} from "date-fns";
import {FieldType} from "../../common/constants";
import {FilterModal} from "../../components/Modals/FilterModal";
import {OrderHistoryActionMenuConfig, OrderHistorySelectionToolbar, StatusFilterOption} from "./OrderHistorySelectionToolbar";
import {OrderHistoryLegacyFilters} from "./OrderHistoryLegacyFilters";
import {ColumnFiltersState} from "@tanstack/react-table";

export type OrderHistoryFiltersMode = "legacy" | "modern";

type OrderHistoryFiltersProps = {
    isMobile: boolean;
    columnFilters: ColumnFiltersState;
    setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;
    showDealershipColumn: boolean;
    showAmountColumn: boolean;
    showStatusColumn: boolean;
    statusFilterOptions: StatusFilterOption[];
    currentSortLabel: string;
    onResetSort: () => void;
    isExcluded: string;
    onExcludedChange: (value: string) => void;
    countSelected: number;
    onClearSelection: () => void;
    selectedItems: { id: number }[];
    onRemoveSelectedOrder: (item: { id: number }) => void;
    actionMenu?: OrderHistoryActionMenuConfig;
    /** Только админ: кнопка Card paid + бейдж; для остальных не передавать */
    showCardPaidStatusFilter?: boolean;
    cardPaidOrderCount?: number | null;
    isCardPaidFilterActive?: boolean;
    onCardPaidFilterClick?: () => void;
    dealershipSuggestions: string[];
    onHelpersReady: (helpers: {
        getFilterValue: (key: string) => string;
        getFilterIconColor: (columnId: string) => string;
        onHeaderFilterClick: (event: React.MouseEvent<HTMLElement>, columnId: string) => void;
        /** Воронки в шапке колонок только в legacy UI; в modern фильтры через панель */
        showColumnHeaderFunnels: boolean;
    }) => void;
};

export const OrderHistoryFilters: FC<OrderHistoryFiltersProps> = ({
    isMobile,
    columnFilters,
    setColumnFilters,
    showDealershipColumn,
    showAmountColumn,
    showStatusColumn,
    statusFilterOptions,
    currentSortLabel,
    onResetSort,
    isExcluded,
    onExcludedChange,
    countSelected,
    onClearSelection,
    selectedItems,
    onRemoveSelectedOrder,
    actionMenu,
    showCardPaidStatusFilter,
    cardPaidOrderCount,
    isCardPaidFilterActive,
    onCardPaidFilterClick,
    dealershipSuggestions,
    onHelpersReady,
}) => {
    const [mode, setMode] = useState<OrderHistoryFiltersMode>("legacy");
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);
    const [dateFromPicker, setDateFromPicker] = useState<Date | null>(null);
    const [dateToPicker, setDateToPicker] = useState<Date | null>(null);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [filterModalColumn, setFilterModalColumn] = useState<string | null>(null);
    const [filterModalPosition, setFilterModalPosition] = useState({top: 0, left: 0});

    const filterValues = useMemo(() => {
        return columnFilters.reduce<Record<string, string>>((acc, item) => {
            acc[item.id] = (item.value as string) || "";
            return acc;
        }, {});
    }, [columnFilters]);

    const getFilterValue = useCallback((key: string): string => {
        return filterValues[key] || "";
    }, [filterValues]);

    const setColumnFilterValue = useCallback((columnId: string, value: string) => {
        setColumnFilters((prev) => {
            const next = prev.filter((item) => item.id !== columnId);
            if (value !== "") {
                next.push({id: columnId, value});
            }
            return next;
        });
    }, [setColumnFilters]);

    const resetAllFilters = useCallback(() => {
        setColumnFilters([]);
        setDateFromPicker(null);
        setDateToPicker(null);
    }, [setColumnFilters]);

    const dateFromFilter = getFilterValue("dateFrom");
    const dateToFilter = getFilterValue("dateTo");

    const legacyDateRangeValue = useMemo(() => {
        const from = dateFromFilter;
        const to = dateToFilter;
        if (!from && !to) return "";
        return `${from || ""}  To: ${to || ""}`.trim();
    }, [dateFromFilter, dateToFilter]);

    const applyLegacyFilterValue = useCallback((val: Record<string, string>) => {
        const [key, raw] = Object.entries(val)[0] || [];
        if (!key) return;
        const value = (raw || "").trim();
        if (key === "date") {
            if (!value) {
                setColumnFilterValue("dateFrom", "");
                setColumnFilterValue("dateTo", "");
                return;
            }
            const [fromRaw, toRaw] = value.split("To:");
            setColumnFilterValue("dateFrom", (fromRaw || "").trim());
            setColumnFilterValue("dateTo", (toRaw || "").trim());
            return;
        }
        setColumnFilterValue(key, value);
    }, [setColumnFilterValue]);

    const closeFilterModal = useCallback(() => {
        setIsFilterModalOpen(false);
        setFilterModalColumn(null);
    }, []);

    const setNewFilterValueFromModal = useCallback((val: any) => {
        if (!filterModalColumn) return;
        const rawValue = (val?.[filterModalColumn] ?? "").toString();
        if (filterModalColumn === "date") {
            if (!rawValue.trim()) {
                setColumnFilterValue("dateFrom", "");
                setColumnFilterValue("dateTo", "");
                closeFilterModal();
                return;
            }
            const [fromRaw, toRaw] = rawValue.split("To:");
            setColumnFilterValue("dateFrom", (fromRaw || "").trim());
            setColumnFilterValue("dateTo", (toRaw || "").trim());
            closeFilterModal();
            return;
        }
        setColumnFilterValue(filterModalColumn, rawValue);
        closeFilterModal();
    }, [closeFilterModal, filterModalColumn, setColumnFilterValue]);

    const getModalFieldType = useCallback((columnId: string): FieldType => {
        if (columnId === "status") return FieldType.Select;
        if (columnId === "date") return FieldType.DateRange;
        return FieldType.Input;
    }, []);

    const getModalOptions = useCallback((columnId: string) => {
        if (columnId === "status") {
            return statusFilterOptions.map((option) => ({
                id: option.id < 0 ? "" : String(option.id),
                name: option.name || "All statuses",
            }));
        }
        return undefined;
    }, [statusFilterOptions]);

    const getFilterIconColor = useCallback((columnId: string): string => {
        if (columnId === "date") {
            return getFilterValue("dateFrom") || getFilterValue("dateTo") ? "blue.500" : "gray.400";
        }
        return getFilterValue(columnId) ? "blue.500" : "gray.400";
    }, [getFilterValue]);

    const onHeaderFilterClick = useCallback((event: React.MouseEvent<HTMLElement>, columnId: string) => {
        const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
        setFilterModalPosition({
            top: rect.bottom,
            left: isMobile ? rect.left - 40 : rect.left,
        });
        setFilterModalColumn(columnId);
        setIsFilterModalOpen(true);
    }, [isMobile]);

    useEffect(() => {
        const parseFilterDate = (value: string): Date | null => {
            if (!value) return null;
            const parsed = parse(value, "MM-dd-yyyy", new Date());
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        };

        setDateFromPicker(parseFilterDate(dateFromFilter));
        setDateToPicker(parseFilterDate(dateToFilter));
    }, [dateFromFilter, dateToFilter]);

    useEffect(() => {
        onHelpersReady({
            getFilterValue,
            getFilterIconColor,
            onHeaderFilterClick,
            showColumnHeaderFunnels: mode === "legacy",
        });
    }, [getFilterIconColor, getFilterValue, mode, onHeaderFilterClick, onHelpersReady]);

    return (
        <>
            <FilterModal
                isOpen={isFilterModalOpen}
                onClose={closeFilterModal}
                buttonPosition={filterModalPosition}
                setNewFilterValue={setNewFilterValueFromModal}
                uniqueName={filterModalColumn || ""}
                fieldType={getModalFieldType(filterModalColumn || "order")}
                options={getModalOptions(filterModalColumn || "order")}
            />
            {mode === "legacy" && (
                <OrderHistoryLegacyFilters
                    isMobile={isMobile}
                    showDealershipColumn={showDealershipColumn}
                    showStatusColumn={showStatusColumn}
                    showAmountColumn={showAmountColumn}
                    statusFilterOptions={statusFilterOptions}
                    getFilterValue={getFilterValue}
                    legacyDateRangeValue={legacyDateRangeValue}
                    applyLegacyFilterValue={applyLegacyFilterValue}
                />
            )}
            <OrderHistorySelectionToolbar
                showFilterPanel={mode === "modern"}
                isFiltersOpen={isFiltersOpen}
                onFiltersOpenToggle={() => setIsFiltersOpen((prev) => !prev)}
                getFilterValue={getFilterValue}
                setColumnFilterValue={setColumnFilterValue}
                resetAllFilters={resetAllFilters}
                dateFromPicker={dateFromPicker}
                dateToPicker={dateToPicker}
                setDateFromPicker={setDateFromPicker}
                setDateToPicker={setDateToPicker}
                dealershipSuggestions={dealershipSuggestions}
                showDealershipColumn={showDealershipColumn}
                showAmountColumn={showAmountColumn}
                showStatusColumn={showStatusColumn}
                statusFilterOptions={statusFilterOptions}
                currentSortLabel={currentSortLabel}
                onResetSort={onResetSort}
                isExcluded={isExcluded}
                onExcludedChange={onExcludedChange}
                countSelected={countSelected}
                onClearSelection={onClearSelection}
                selectedItems={selectedItems}
                onRemoveSelectedOrder={onRemoveSelectedOrder}
                isMobile={isMobile}
                uiModeIsLegacy={mode === "legacy"}
                onUiModeToggle={(isLegacy) => setMode(isLegacy ? "legacy" : "modern")}
                actionMenu={actionMenu}
                showCardPaidStatusFilter={showCardPaidStatusFilter}
                cardPaidOrderCount={cardPaidOrderCount}
                isCardPaidFilterActive={isCardPaidFilterActive}
                onCardPaidFilterClick={onCardPaidFilterClick}
            />
        </>
    );
};

export default OrderHistoryFilters;
